import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, Upload, FileText, Plus, CheckCircle2, Circle, X, Tags } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DocumentCategory, DOCUMENT_CATEGORIES, EFFECTIVE_DATE_CATEGORIES, InvestorDocument, Investor } from '@/types/investor';
import { InvestorService } from '@/services/investorService';
import { DocumentService } from '@/services/documentService';
import { InvestorDocumentTypeService, type InvestorDocumentType } from '@/services/investorDocumentTypeService';
import { useInvestorDocuments, DocumentGroup } from '@/hooks/useInvestorDocuments';
import { DocumentCategoryCard } from './DocumentCategoryCard';
import { DocumentUploadModal } from './DocumentUploadModal';
import { ManageDocumentTypesModal } from './ManageDocumentTypesModal';
import { useAuth } from '@/contexts/useAuth';
import { DOC_VISIBILITY_ROLES, hiddenRolesFromAllowedRoles } from '@/utils/investor/documentVisibility';
import { resolveInvestorDocumentLabel } from '@/utils/investor/linkedDocumentLabel';
import { toast } from 'sonner';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('InvestorDocumentsPage', 'investor-documents');

const REQUIRED_WHOLESALE_CATEGORIES: DocumentCategory[] = ['Broker Agreement'];
const REQUIRED_CORRESPONDENT_CATEGORIES: DocumentCategory[] = ['Signed MLPA', 'Seller Guide'];

interface InvestorDocumentsPageProps {
  investorId: string;
  organizationId: string;
  investor: Investor;
  readOnly?: boolean;
  /** UI-level role visibility filter. When provided, hides documents the predicate rejects. */
  isDocumentVisible?: (doc: InvestorDocument) => boolean;
  onUploadPendingChange?: (pending: boolean) => void;
}

type ChannelFilter = 'all' | 'wholesale' | 'correspondent';

export const InvestorDocumentsPage: React.FC<InvestorDocumentsPageProps> = ({
  investorId,
  organizationId,
  investor,
  readOnly = false,
  isDocumentVisible,
  onUploadPendingChange
}) => {
  const { user } = useAuth();
  const isStrictAdmin = useMemo(
    () => (((user as { roles?: string[] } | null)?.roles ?? []) as string[]).map((role) => role.toLowerCase()).includes('admin'),
    [user]
  );
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState<InvestorDocument | null>(null);
  const [editTarget, setEditTarget] = useState<InvestorDocument | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editEffectiveDate, setEditEffectiveDate] = useState('');
  const [editLinkedTabs, setEditLinkedTabs] = useState<('processing' | 'closing')[]>([]);
  const [editHiddenRoles, setEditHiddenRoles] = useState<string[]>([]);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [uploadDocumentType, setUploadDocumentType] = useState<'wholesale' | 'correspondent'>(
    investor.canGoCorrespondent ? 'correspondent' : 'wholesale'
  );
  // Item 11: persisted document-type catalog (alphabetized; falls back to built-ins on load error).
  const [catalog, setCatalog] = useState<InvestorDocumentType[]>([]);
  const [showManageTypes, setShowManageTypes] = useState(false);
  const loadCatalog = useCallback(async () => {
    try {
      setCatalog(await InvestorDocumentTypeService.list());
    } catch (error) {
      log.error('Failed to load document type catalog', { error });
    }
  }, []);
  useEffect(() => {
 void loadCatalog(); 
}, [loadCatalog]);
  const availableCategories = useMemo<DocumentCategory[]>(
    () => (catalog.length > 0
      ? catalog.map((t) => t.name)
      : [...DOCUMENT_CATEGORIES].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))),
    [catalog]
  );

  const wholesaleDocs = useInvestorDocuments(investorId, 'wholesale');
  const correspondentDocs = useInvestorDocuments(investorId, 'correspondent');

  // Apply the UI-level role visibility filter to the raw doc lists so tab counts and
  // the required-documents checklist also respect hidden_roles (not just rendered rows).
  const visibleWholesaleDocs = useMemo(
    () => (isDocumentVisible ? wholesaleDocs.documents.filter(isDocumentVisible) : wholesaleDocs.documents),
    [wholesaleDocs.documents, isDocumentVisible]
  );
  const visibleCorrespondentDocs = useMemo(
    () => (isDocumentVisible ? correspondentDocs.documents.filter(isDocumentVisible) : correspondentDocs.documents),
    [correspondentDocs.documents, isDocumentVisible]
  );

  // Load documents on mount
  useEffect(() => {
    if (investor.canDoWholesale) {
wholesaleDocs.reload();
}
    if (investor.canGoCorrespondent) {
correspondentDocs.reload();
}
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-load only on investor change; the doc hooks change identity each render and broader deps would loop
  }, [investorId]);

  // Combine and filter groups based on channel and search
  const filteredGroups = useMemo((): { channel: 'wholesale' | 'correspondent'; group: DocumentGroup }[] => {
    const groups: { channel: 'wholesale' | 'correspondent'; group: DocumentGroup }[] = [];

    if ((channelFilter === 'all' || channelFilter === 'wholesale') && investor.canDoWholesale) {
      for (const group of wholesaleDocs.groupedDocuments) {
        groups.push({ channel: 'wholesale', group });
      }
    }

    if ((channelFilter === 'all' || channelFilter === 'correspondent') && investor.canGoCorrespondent) {
      for (const group of correspondentDocs.groupedDocuments) {
        groups.push({ channel: 'correspondent', group });
      }
    }

    // UI-level role visibility filter (the predicate already grants admin/partner and
    // treats empty hidden_roles as "all"). Filter the documents WITHIN each lineage so
    // hidden sibling docs don't inflate counts or render in cards; drop empty groups.
    const visibleGroups = isDocumentVisible
      ? groups
          .map(({ channel, group }) => ({
            channel,
            group: { ...group, documents: group.documents.filter(isDocumentVisible) }
          }))
          .filter(({ group }) => group.documents.length > 0)
      : groups;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return visibleGroups.filter(({ group }) => {
        const latestDoc = group.documents[0];
        return (
          group.category.toLowerCase().includes(query) ||
          (group.subType && group.subType.toLowerCase().includes(query)) ||
          (latestDoc?.originalFileName && latestDoc.originalFileName.toLowerCase().includes(query)) ||
          (latestDoc?.displayName && latestDoc.displayName.toLowerCase().includes(query)) ||
          (latestDoc?.notes && latestDoc.notes.toLowerCase().includes(query))
        );
      });
    }

    return visibleGroups;
  }, [channelFilter, searchQuery, wholesaleDocs.groupedDocuments, correspondentDocs.groupedDocuments, investor, isDocumentVisible]);

  // Group filteredGroups by base category for section headers
  const categorySections = useMemo(() => {
    const sections: { category: string; items: typeof filteredGroups }[] = [];
    const categoryMap = new Map<string, typeof filteredGroups>();

    for (const item of filteredGroups) {
      const cat = item.group.category;
      if (!categoryMap.has(cat)) {
        const items: typeof filteredGroups = [];
        categoryMap.set(cat, items);
        sections.push({ category: cat, items });
      }
      categoryMap.get(cat)!.push(item);
    }

    return sections.sort((a, b) => a.category.localeCompare(b.category));
  }, [filteredGroups]);

  // Totals for tab badges (visibility-filtered)
  const wholesaleCount = visibleWholesaleDocs.length;
  const correspondentCount = visibleCorrespondentDocs.length;
  const totalCount = wholesaleCount + correspondentCount;

  const handleArchive = useCallback(async (doc: InvestorDocument) => {
    // eslint-disable-next-line no-alert -- intentional native confirm for a destructive archive action
    if (!window.confirm(`Archive ${resolveInvestorDocumentLabel(doc)} (v${doc.version})?`)) {
return;
}
    try {
      await InvestorService.archiveInvestorDocument(doc.id, organizationId);
      toast.success('Document archived');
      wholesaleDocs.reload();
      correspondentDocs.reload();
    } catch (error) {
      log.error('Failed to archive document', { error });
      toast.error('Failed to archive document');
    }
  }, [organizationId, wholesaleDocs, correspondentDocs]);

  const reloadDocuments = useCallback(async () => {
    await Promise.all([wholesaleDocs.reload(), correspondentDocs.reload()]);
  }, [wholesaleDocs, correspondentDocs]);

  const handleEdit = useCallback((doc: InvestorDocument) => {
    setEditTarget(doc);
    setEditDisplayName(doc.displayName?.trim() ?? '');
    setEditNotes(doc.notes ?? '');
    setEditEffectiveDate(doc.effectiveDate ?? '');
    setEditLinkedTabs(doc.linkedTabs ?? []);
    setEditHiddenRoles(doc.hiddenRoles ?? hiddenRolesFromAllowedRoles(doc.allowedRoles));
  }, []);

  const toggleEditLinkedTab = useCallback((tab: 'processing' | 'closing', checked: boolean) => {
    setEditLinkedTabs((prev) => checked
      ? Array.from(new Set([...prev, tab]))
      : prev.filter((value) => value !== tab)
    );
  }, []);

  const toggleEditRoleVisibility = useCallback((role: string, checked: boolean) => {
    setEditHiddenRoles((prev) => checked
      ? prev.filter((value) => value !== role)
      : Array.from(new Set([...prev, role]))
    );
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editTarget) {
      return;
    }
    const displayName = editDisplayName.trim() || null;
    setIsSavingEdit(true);
    try {
      await DocumentService.updateDocumentMetadata(editTarget.id, organizationId, {
        displayName,
        notes: editNotes.trim() || null,
        effectiveDate: EFFECTIVE_DATE_CATEGORIES.includes(editTarget.category) ? editEffectiveDate || null : undefined,
        linkedTabs: editLinkedTabs,
        hiddenRoles: editHiddenRoles
      });
      await reloadDocuments();
      setEditTarget(null);
      toast.success('Document updated');
    } catch (error) {
      log.error('Failed to update document metadata', { error });
      toast.error('Failed to update document');
    } finally {
      setIsSavingEdit(false);
    }
  }, [editDisplayName, editEffectiveDate, editHiddenRoles, editLinkedTabs, editNotes, editTarget, organizationId, reloadDocuments]);

  const handleReplace = useCallback((doc: InvestorDocument) => {
    setReplaceTarget(doc);
    setUploadDocumentType(doc.documentType as 'wholesale' | 'correspondent');
    setShowUploadModal(true);
  }, []);

  const handleUploadComplete = useCallback(() => {
    wholesaleDocs.reload();
    correspondentDocs.reload();
    setReplaceTarget(null);
  }, [wholesaleDocs, correspondentDocs]);

  const openUploadModal = useCallback(() => {
    setReplaceTarget(null);
    // Default to the first available channel
    if (channelFilter !== 'all') {
      setUploadDocumentType(channelFilter);
    } else if (investor.canGoCorrespondent) {
      setUploadDocumentType('correspondent');
    } else {
      setUploadDocumentType('wholesale');
    }
    setShowUploadModal(true);
  }, [channelFilter, investor]);

  const isLoading = wholesaleDocs.isLoading || correspondentDocs.isLoading;

  // Compute missing required document categories for the current channel filter.
  const missingCategories = useMemo(() => {
    const getMissing = (categories: DocumentCategory[], docs: InvestorDocument[]) => {
      const uploadedCategories = new Set(docs.map(d => d.category));
      return categories.filter(c => !uploadedCategories.has(c));
    };

    if (channelFilter === 'wholesale' && investor.canDoWholesale) {
      return { wholesale: getMissing(REQUIRED_WHOLESALE_CATEGORIES, visibleWholesaleDocs), correspondent: [] as DocumentCategory[] };
    }
    if (channelFilter === 'correspondent' && investor.canGoCorrespondent) {
      return { wholesale: [] as DocumentCategory[], correspondent: getMissing(REQUIRED_CORRESPONDENT_CATEGORIES, visibleCorrespondentDocs) };
    }
    return {
      wholesale: investor.canDoWholesale ? getMissing(REQUIRED_WHOLESALE_CATEGORIES, visibleWholesaleDocs) : [],
      correspondent: investor.canGoCorrespondent ? getMissing(REQUIRED_CORRESPONDENT_CATEGORIES, visibleCorrespondentDocs) : []
    };
  }, [channelFilter, visibleWholesaleDocs, visibleCorrespondentDocs, investor]);

  const hasMissingRequiredDocuments = missingCategories.wholesale.length > 0 || missingCategories.correspondent.length > 0;

  const getHookForChannel = (channel: 'wholesale' | 'correspondent') =>
    channel === 'wholesale' ? wholesaleDocs : correspondentDocs;

  return (
    <div className="space-y-4 lw-detail-stack">
      {/* Search + Upload Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        {isStrictAdmin && (
          <button
            onClick={() => setShowManageTypes(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-md text-foreground hover:bg-muted/50 transition-colors flex-shrink-0"
            title="Manage document types"
          >
            <Tags className="h-4 w-4" aria-hidden="true" />
            Manage types
          </button>
        )}
        {!readOnly && (
          <button
            onClick={openUploadModal}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors flex-shrink-0"
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            Upload documents
          </button>
        )}
      </div>

      {/* Channel Tabs */}
      <Tabs value={channelFilter} onValueChange={(v) => setChannelFilter(v as ChannelFilter)}>
        <TabsList className="grid w-full grid-cols-3" aria-label="Filter by channel">
          <TabsTrigger value="all">
            All <span className="ml-1 text-xs text-muted-foreground">({totalCount})</span>
          </TabsTrigger>
          {investor.canDoWholesale && (
            <TabsTrigger value="wholesale">
              Wholesale <span className="ml-1 text-xs text-muted-foreground">({wholesaleCount})</span>
            </TabsTrigger>
          )}
          {investor.canGoCorrespondent && (
            <TabsTrigger value="correspondent">
              Correspondent <span className="ml-1 text-xs text-muted-foreground">({correspondentCount})</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Content area (shared across all tabs) */}
        <TabsContent value={channelFilter} className="mt-4 lw-detail-separated">
          {isLoading && (
            <div className="flex items-center justify-center py-12 lw-detail-empty">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          )}

          {!isLoading && filteredGroups.length > 0 && (
            <div className="space-y-2">
              {categorySections.map(({ category, items }) => (
                <section key={category} className="space-y-1" aria-labelledby={`document-category-${category.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}>
                  {/* Category section header */}
                  <h3
                    id={`document-category-${category.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                    className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    {category}
                    <span className="ml-1.5 normal-case tracking-normal">
                      ({items.reduce((sum, i) => sum + i.group.documents.length, 0)})
                    </span>
                  </h3>
                  {/* Compact document rows within this category */}
                  <div className="divide-y divide-border overflow-hidden rounded-lg border border-border">
                    {items.map(({ channel, group }) => {
                      const hook = getHookForChannel(channel);
                      return (
                        <DocumentCategoryCard
                          key={`${channel}-${group.groupKey}`}
                          documents={group.documents}
                          allVersions={
                            isDocumentVisible && hook.versionHistory[group.groupKey]
                              ? hook.versionHistory[group.groupKey].filter(isDocumentVisible)
                              : hook.versionHistory[group.groupKey]
                          }
                          versionHistoryLoading={hook.versionHistoryLoading[group.groupKey]}
                          isExpanded={hook.expandedGroups.has(group.groupKey)}
                          channel={channelFilter === 'all' && investor.canDoWholesale && investor.canGoCorrespondent ? channel : undefined}
                          readOnly={readOnly}
                          showRestrictedBadge={isStrictAdmin}
                          onEdit={handleEdit}
                          onReplace={handleReplace}
                          onArchive={handleArchive}
                          onToggleVersionHistory={() => hook.toggleExpanded(group.groupKey, group.category, group.subType)}
                        />
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}

          {!isLoading && filteredGroups.length === 0 && (
            <div className="text-center py-12 bg-muted/20 rounded-lg border border-border lw-detail-empty">
              <FileText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" aria-hidden="true" />
              {searchQuery.trim() ? (
                <>
                  <h3 className="text-sm font-medium text-foreground mb-1">No matching documents</h3>
                  <p className="text-sm text-muted-foreground">
                    No documents match &ldquo;{searchQuery}&rdquo;
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-sm font-medium text-foreground mb-1">No documents uploaded yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Upload your first document to get started
                  </p>
                  {!readOnly && (
                    <button
                      onClick={openUploadModal}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      Upload First Document
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Missing Documents Checklist */}
          {!isLoading && !searchQuery.trim() && hasMissingRequiredDocuments && (
            <div className="mt-4 border border-border rounded-lg bg-muted/10 p-4 lw-detail-separated lw-document-checklist">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                Required Documents
              </h4>
              <div className="space-y-3">
                {missingCategories.wholesale.length > 0 && (channelFilter === 'all' || channelFilter === 'wholesale') && (
                  <div>
                    {channelFilter === 'all' && investor.canDoWholesale && investor.canGoCorrespondent && (
                      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Wholesale</div>
                    )}
                    <div className="grid grid-cols-2 gap-1">
                      {REQUIRED_WHOLESALE_CATEGORIES.map(cat => {
                        const isUploaded = !missingCategories.wholesale.includes(cat);
                        return (
                          <div key={`w-${cat}`} className="flex items-center gap-1.5 text-xs">
                            {isUploaded ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" aria-hidden="true" />
                            ) : (
                              <Circle className="h-3.5 w-3.5 text-muted-foreground/40 flex-shrink-0" aria-hidden="true" />
                            )}
                            <span className={isUploaded ? 'text-muted-foreground' : 'text-foreground'}>{cat}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {missingCategories.correspondent.length > 0 && (channelFilter === 'all' || channelFilter === 'correspondent') && (
                  <div>
                    {channelFilter === 'all' && investor.canDoWholesale && investor.canGoCorrespondent && (
                      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Correspondent</div>
                    )}
                    <div className="grid grid-cols-2 gap-1">
                      {REQUIRED_CORRESPONDENT_CATEGORIES.map(cat => {
                        const isUploaded = !missingCategories.correspondent.includes(cat);
                        return (
                          <div key={`c-${cat}`} className="flex items-center gap-1.5 text-xs">
                            {isUploaded ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" aria-hidden="true" />
                            ) : (
                              <Circle className="h-3.5 w-3.5 text-muted-foreground/40 flex-shrink-0" aria-hidden="true" />
                            )}
                            <span className={isUploaded ? 'text-muted-foreground' : 'text-foreground'}>{cat}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {editTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg border border-border bg-background shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Edit Document</h3>
                <p className="text-xs text-muted-foreground">
                  Current title: {resolveInvestorDocumentLabel(editTarget)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditTarget(null)}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Close edit document dialog"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-4 py-4 lw-detail-stack">
              <div className="space-y-1">
                <label htmlFor="document-display-name" className="text-xs font-medium text-muted-foreground">
                  Custom display name (optional)
                </label>
                <input
                  id="document-display-name"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to use the file name.
                </p>
                <p className="text-xs text-muted-foreground">
                  File name: {editTarget.originalFileName}
                </p>
              </div>

              {EFFECTIVE_DATE_CATEGORIES.includes(editTarget.category) && (
                <div className="space-y-1">
                  <label htmlFor="document-effective-date" className="text-xs font-medium text-muted-foreground">
                    Effective Date
                  </label>
                  <input
                    id="document-effective-date"
                    type="date"
                    value={editEffectiveDate}
                    onChange={(e) => setEditEffectiveDate(e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              )}

              <div className="space-y-1">
                <label htmlFor="document-notes" className="text-xs font-medium text-muted-foreground">
                  Notes
                </label>
                <textarea
                  id="document-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <fieldset className="rounded-md border border-border p-3">
                <legend className="px-1 text-xs font-medium text-muted-foreground">Linked Tabs</legend>
                <div className="mt-1 flex flex-wrap gap-4">
                  {(['processing', 'closing'] as const).map((tab) => (
                    <label key={tab} className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={editLinkedTabs.includes(tab)}
                        onChange={(e) => toggleEditLinkedTab(tab, e.target.checked)}
                        className="rounded border-border text-primary focus:ring-ring"
                      />
                      {tab === 'processing' ? 'Processing' : 'Closing'}
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset className="rounded-md border border-border p-3">
                <legend className="px-1 text-xs font-medium text-muted-foreground">Visibility</legend>
                <p className="mb-2 text-xs text-muted-foreground">
                  Checked roles can see this document. Admins and partners can always see investor documents.
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {['Admin', 'Partner'].map((role) => (
                    <label key={role} className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked
                        disabled
                        className="rounded border-border text-primary disabled:opacity-60"
                      />
                      {role}
                    </label>
                  ))}
                  {DOC_VISIBILITY_ROLES.map((role) => (
                    <label key={role.value} className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={!editHiddenRoles.includes(role.value)}
                        onChange={(e) => toggleEditRoleVisibility(role.value, e.target.checked)}
                        className="rounded border-border text-primary focus:ring-ring"
                      />
                      {role.label}
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>

            <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
              <button
                type="button"
                onClick={() => setEditTarget(null)}
                className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted"
                disabled={isSavingEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSaveEdit()}
                className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                disabled={isSavingEdit}
              >
                {isSavingEdit ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <DocumentUploadModal
        investorId={investorId}
        organizationId={organizationId}
        documentType={uploadDocumentType}
        channels={[
          ...(investor.canDoWholesale ? ['wholesale' as const] : []),
          ...(investor.canGoCorrespondent ? ['correspondent' as const] : [])
        ]}
        defaultChannel={channelFilter === 'all' && investor.canDoWholesale && investor.canGoCorrespondent ? '' : uploadDocumentType}
        onPendingChange={onUploadPendingChange}
        availableCategories={availableCategories}
        isOpen={showUploadModal}
        onClose={() => {
 setShowUploadModal(false); setReplaceTarget(null);
}}
        onUploadComplete={handleUploadComplete}
        replaceTarget={replaceTarget}
      />

      {showManageTypes && (
        <ManageDocumentTypesModal
          organizationId={organizationId}
          types={catalog}
          onClose={() => setShowManageTypes(false)}
          onChanged={() => {
 void loadCatalog(); 
}}
        />
      )}
    </div>
  );
};
